import { Component } from '@angular/core';

type FaqCategoryId = 'all' | 'candidate' | 'cv' | 'jobs' | 'companies' | 'account';

interface FaqCategory {
  id: FaqCategoryId;
  labelKey: string;
}

interface FaqItem {
  id: number;
  iconImg: string;
  category: Exclude<FaqCategoryId, 'all'>;
  questionKey: string;
  answerKey: string;
  searchKeys: string[];
}

@Component({
  selector: 'app-faqs',
  templateUrl: './faqs.component.html',
  styleUrl: './faqs.component.scss'
})
export class FaqsComponent {
  searchTerm = '';
  activeCategory: FaqCategoryId = 'all';

  private openFaqIds = new Set<number>([1]);

  categories: FaqCategory[] = [
    { id: 'all', labelKey: 'atw.faqs.categories.all' },
    { id: 'candidate', labelKey: 'atw.faqs.categories.candidate' },
    { id: 'cv', labelKey: 'atw.faqs.categories.cv' },
    { id: 'jobs', labelKey: 'atw.faqs.categories.jobs' },
    { id: 'companies', labelKey: 'atw.faqs.categories.companies' },
    { id: 'account', labelKey: 'atw.faqs.categories.account' }
  ];

  faqs: FaqItem[] = [
    {
      id: 1,
      iconImg: 'assets/as/51.webp',
      category: 'candidate',
      questionKey: 'atw.faqs.items.faq1.question',
      answerKey: 'atw.faqs.items.faq1.answer',
      searchKeys: ['candidate', 'apply', 'register', 'تقديم', 'مرشح', 'تسجيل']
    },
    {
      id: 2,
      iconImg: 'assets/as/12.webp',
      category: 'cv',
      questionKey: 'atw.faqs.items.faq2.question',
      answerKey: 'atw.faqs.items.faq2.answer',
      searchKeys: ['cv', 'resume', 'pdf', 'سيرة ذاتية', 'سي في']
    },
    {
      id: 3,
      iconImg: 'assets/as/52.webp',
      category: 'cv',
      questionKey: 'atw.faqs.items.faq3.question',
      answerKey: 'atw.faqs.items.faq3.answer',
      searchKeys: ['keywords', 'matching', 'skills', 'كلمات مفتاحية', 'مهارات']
    },
    {
      id: 4,
      iconImg: 'assets/as/4.webp',
      category: 'jobs',
      questionKey: 'atw.faqs.items.faq4.question',
      answerKey: 'atw.faqs.items.faq4.answer',
      searchKeys: ['jobs', 'recommended jobs', 'matching jobs', 'وظائف', 'ترشيحات']
    },
    {
      id: 5,
      iconImg: 'assets/as/1.webp',
      category: 'jobs',
      questionKey: 'atw.faqs.items.faq5.question',
      answerKey: 'atw.faqs.items.faq5.answer',
      searchKeys: ['travel', 'abroad', 'work abroad', 'سفر', 'عمل بالخارج']
    },
    {
      id: 6,
      iconImg: 'assets/as/9.webp',
      category: 'companies',
      questionKey: 'atw.faqs.items.faq6.question',
      answerKey: 'atw.faqs.items.faq6.answer',
      searchKeys: ['company', 'employer', 'hire', 'شركة', 'توظيف']
    },
    {
      id: 7,
      iconImg: 'assets/as/37.webp',
      category: 'account',
      questionKey: 'atw.faqs.items.faq7.question',
      answerKey: 'atw.faqs.items.faq7.answer',
      searchKeys: ['account', 'data', 'privacy', 'حساب', 'بيانات', 'خصوصية']
    },
    {
      id: 8,
      iconImg: 'assets/as/41.webp',
      category: 'candidate',
      questionKey: 'atw.faqs.items.faq8.question',
      answerKey: 'atw.faqs.items.faq8.answer',
      searchKeys: ['time', 'response', 'reply', 'وقت', 'رد', 'متابعة']
    },
    {
      id: 9,
      iconImg: 'assets/as/12.webp',
      category: 'companies',
      questionKey: 'atw.faqs.items.faq9.question',
      answerKey: 'atw.faqs.items.faq9.answer',
      searchKeys: ['post job', 'publish job', 'announce', 'نشر وظيفة', 'إعلان وظيفة']
    },
    {
      id: 10,
      iconImg: 'assets/as/46.webp',
      category: 'account',
      questionKey: 'atw.faqs.items.faq10.question',
      answerKey: 'atw.faqs.items.faq10.answer',
      searchKeys: ['support', 'contact', 'help', 'دعم', 'تواصل', 'مساعدة']
    }
  ];

  get filteredFaqs(): FaqItem[] {
    const term = this.searchTerm.trim().toLowerCase();

    return this.faqs.filter(faq => {
      const matchesCategory =
        this.activeCategory === 'all' || faq.category === this.activeCategory;

      const searchableText = [
        faq.category,
        faq.questionKey,
        faq.answerKey,
        ...faq.searchKeys
      ]
        .join(' ')
        .toLowerCase();

      const matchesSearch = !term || searchableText.includes(term);

      return matchesCategory && matchesSearch;
    });
  }

  onSearch(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.searchTerm = input.value;
  }

  setCategory(category: FaqCategoryId): void {
    this.activeCategory = category;
  }

  toggleFaq(id: number): void {
    const nextOpenFaqIds = new Set(this.openFaqIds);

    if (nextOpenFaqIds.has(id)) {
      nextOpenFaqIds.delete(id);
    } else {
      nextOpenFaqIds.add(id);
    }

    this.openFaqIds = nextOpenFaqIds;
  }

  isOpen(id: number): boolean {
    return this.openFaqIds.has(id);
  }

  openAll(): void {
    this.openFaqIds = new Set(this.filteredFaqs.map(faq => faq.id));
  }

  closeAll(): void {
    this.openFaqIds = new Set<number>();
  }

  resetFilters(): void {
    this.searchTerm = '';
    this.activeCategory = 'all';
    this.openFaqIds = new Set<number>([1]);
  }

  trackByFaq(index: number, faq: FaqItem): number {
    return faq.id;
  }

  trackByCategory(index: number, category: FaqCategory): string {
    return category.id;
  }
}